/** Helpers for pet photo (q1) file collection and PMC required-flag checks. */

export type PhotoUploadOutcome = 'none' | 'success' | 'failed' | 'missing_required';

export interface RegistrationPhotoResult {
  photoUploaded?: boolean;
  photoAttempted?: boolean;
}

export function collectPetPhotoFiles(pet: unknown): File[] {
  if (!pet || typeof pet !== 'object') return [];
  const p = pet as {
    photos?: Array<{ file?: File | null }>;
    photoFile?: File | null;
  };
  if (Array.isArray(p.photos)) {
    return p.photos
      .filter((entry) => entry?.file instanceof File)
      .map((entry) => entry.file as File);
  }
  if (p.photoFile instanceof File) return [p.photoFile];
  return [];
}

/** True when preview is already a server URL (draft restore), not a local blob. */
export function hasPersistedPetPhotoPreview(pet: unknown): boolean {
  if (!pet || typeof pet !== 'object') return false;
  const preview = (pet as { photos?: Array<{ preview?: string | null }> }).photos?.[0]?.preview;
  return (
    typeof preview === 'string' &&
    (preview.startsWith('http://') || preview.startsWith('https://'))
  );
}

export function isPetPhotoRequired(propertySettings: unknown): boolean {
  const questionnaire = (propertySettings as { questionnaire?: unknown })?.questionnaire;
  if (!Array.isArray(questionnaire)) return false;
  const q1 = questionnaire.find((q) => (q as { id?: string }).id === 'q1') as
    | { show?: boolean; required?: boolean }
    | undefined;
  if (!q1 || q1.show === false) return false;
  return Boolean(q1.required);
}

export function petPhotoUploadErrorMessage(petName: string): string {
  const label = petName?.trim() || 'your pet';
  return `Registration was saved, but the photo for ${label} could not be uploaded. Open ${label}'s profile from your dashboard and upload the photo again.`;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Encode q1 photo for inline registration payload (same pattern as document fileData). */
export async function preparePetPhotoForRegistration(
  pet: unknown,
): Promise<{ name: string; mimeType: string; fileData: string } | null> {
  const files = collectPetPhotoFiles(pet);
  if (files.length === 0) return null;
  const file = files[0];
  const fileData = await fileToBase64(file);
  return {
    name: file.name,
    mimeType: file.type || 'image/jpeg',
    fileData,
  };
}

/** Derive UI outcome after registerPets — photo is uploaded inside that same request. */
export function photoOutcomeFromRegistration(
  pet: unknown,
  propertySettings: unknown,
  registerResult?: RegistrationPhotoResult,
): PhotoUploadOutcome {
  if (registerResult?.photoUploaded) return 'success';

  const hadPersisted = hasPersistedPetPhotoPreview(pet);
  if (hadPersisted && !collectPetPhotoFiles(pet).length) return 'success';

  if (registerResult?.photoAttempted) return 'failed';

  if (isPetPhotoRequired(propertySettings) && !hadPersisted) {
    return 'missing_required';
  }

  return 'none';
}
